/**
 * StandardsPage - EA-Agile Standards Pipeline View
 * Displays draft standards in pipeline stages: Draft → Review → Approved → Published
 * Standards are fetched from ea_agile.cards with card_type='standard'
 */

import React, { useCallback, useEffect, useState } from 'react';
import { FiPlus, FiRefreshCw, FiFilter } from 'react-icons/fi';
import { useStatus } from '../contexts/StatusProvider';
import NewCardModal from '../components/NewCardModal';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

interface Standard {
  card_id: string;
  card_type: string;
  title: string;
  description?: string;
  focus_category?: string;
  standards_type?: string;
  status: string;
  state?: string;
  priority?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

interface PipelineStage {
  id: string;
  title: string;
  description: string;
  bgColor: string;
  borderColor: string;
  statusValues: string[];
}

// Pipeline stages for standards
const PIPELINE_STAGES: PipelineStage[] = [
  {
    id: 'draft',
    title: 'Draft',
    description: 'New standards awaiting review',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-300',
    statusValues: ['draft']
  },
  {
    id: 'review',
    title: 'Review',
    description: 'Under review by approval groups',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
    statusValues: ['in_review', 'pending_review']
  },
  {
    id: 'approved',
    title: 'Approved',
    description: 'Ready for publication',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-300',
    statusValues: ['approved', 'ready']
  },
  {
    id: 'published',
    title: 'Published',
    description: 'Active published standards',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-300',
    statusValues: ['published', 'active']
  }
];

const StandardsPage: React.FC = () => {
  const { Client_ID, Company_ID, Auth_Status } = useStatus();
  const [standards, setStandards] = useState<Standard[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [categories, setCategories] = useState<Set<string>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Get effective client/company ID (from StatusProvider or localStorage for super admin context)
  const effectiveClientId = Client_ID || localStorage.getItem('selectedClientId');
  const effectiveCompanyId = Company_ID || localStorage.getItem('selectedCompanyId');

  // Fetch standards from API
  const fetchStandards = useCallback(async () => {
    if (!Auth_Status) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await Make_Authorized_API_Call<Standard>(
        API_ENDPOINTS.EA_AGILE.CARDS,
        'GET',
        undefined,
        { params: {
          card_type: 'standard',
          Client_ID: effectiveClientId,
          Company_ID: effectiveCompanyId
        } }
      );

      if (!isSuccessResponse(response)) {
        throw new Error(response.message || 'Failed to fetch standards');
      }

      const standardsList = response.data.Records || [];
      setStandards(standardsList);

      // Extract unique categories
      const uniqueCategories = new Set<string>();
      standardsList.forEach((standard: Standard) => {
        if (standard.focus_category) {
          uniqueCategories.add(standard.focus_category);
        }
      });
      setCategories(uniqueCategories);
    } catch (err) {
      console.error('Error fetching standards:', err);
      setError(err instanceof Error ? err.message : 'Failed to load standards');
    } finally {
      setIsLoading(false);
    }
  }, [Auth_Status]);

  useEffect(() => {
    fetchStandards();
  }, [fetchStandards]);

  // Filter standards based on selected filters
  const getFilteredStandards = (): Standard[] => {
    return standards.filter(standard => {
      if (statusFilter && !PIPELINE_STAGES
        .find(stage => stage.id === statusFilter)
        ?.statusValues.includes(standard.status)) {
        return false;
      }
      if (categoryFilter && standard.focus_category !== categoryFilter) {
        return false;
      }
      return true;
    });
  };

  // Get standards for a specific pipeline stage
  const getStandardsForStage = (stage: PipelineStage): Standard[] => {
    return getFilteredStandards().filter(standard =>
      stage.statusValues.includes(standard.status)
    );
  };

  // Calculate pipeline statistics
  const getPipelineStats = () => {
    return {
      total: getFilteredStandards().length,
      draft: getStandardsForStage(PIPELINE_STAGES[0]).length,
      review: getStandardsForStage(PIPELINE_STAGES[1]).length,
      approved: getStandardsForStage(PIPELINE_STAGES[2]).length,
      published: getStandardsForStage(PIPELINE_STAGES[3]).length
    };
  };

  const stats = getPipelineStats();

  // Render a standard card
  const renderStandardCard = (standard: Standard) => (
    <div
      key={standard.card_id}
      className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
    >
      <div className="mb-2">
        <h4 className="text-sm font-semibold text-gray-900 line-clamp-2">
          {standard.title}
        </h4>
      </div>

      <div className="space-y-1 mb-3">
        {standard.focus_category && (
          <div className="text-xs text-gray-600">
            <span className="font-medium">Category:</span> {standard.focus_category}
          </div>
        )}
        {standard.standards_type && (
          <div className="text-xs text-gray-600">
            <span className="font-medium">Type:</span> {standard.standards_type}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between text-xs">
        <div className="text-gray-500">
          {new Date(standard.created_at).toLocaleDateString()}
        </div>
        <div className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
          {standard.status}
        </div>
      </div>

      {standard.description && (
        <p className="text-xs text-gray-600 mt-3 line-clamp-2">
          {standard.description}
        </p>
      )}
    </div>
  );

  return (
    <div className="p-6 h-full overflow-auto">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Standards Pipeline</h1>
            <p className="text-gray-600 mt-1">
              Manage standards from draft to publication
            </p>
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FiPlus className="h-5 w-5" />
            <span>Create Standard</span>
          </button>
        </div>

        {/* Pipeline Statistics */}
        <div className="grid grid-cols-5 gap-3 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="text-xs text-gray-600 font-medium">Total</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="text-xs text-gray-600 font-medium">Draft</div>
            <div className="text-2xl font-bold text-gray-600 mt-1">{stats.draft}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="text-xs text-gray-600 font-medium">Review</div>
            <div className="text-2xl font-bold text-blue-600 mt-1">{stats.review}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="text-xs text-gray-600 font-medium">Approved</div>
            <div className="text-2xl font-bold text-green-600 mt-1">{stats.approved}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="text-xs text-gray-600 font-medium">Published</div>
            <div className="text-2xl font-bold text-purple-600 mt-1">{stats.published}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-3">
          <button
            onClick={() => fetchStandards()}
            className="flex items-center space-x-1 text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
          >
            <FiRefreshCw className="h-4 w-4" />
            <span className="text-sm">Refresh</span>
          </button>

          <div className="flex items-center space-x-2 bg-white rounded-lg border border-gray-200 px-3 py-2">
            <FiFilter className="h-4 w-4 text-gray-500" />
            <select
              value={statusFilter || ''}
              onChange={(e) => setStatusFilter(e.target.value || null)}
              className="text-sm bg-transparent border-0 focus:outline-none focus:ring-0"
            >
              <option value="">All Statuses</option>
              {PIPELINE_STAGES.map(stage => (
                <option key={stage.id} value={stage.id}>
                  {stage.title}
                </option>
              ))}
            </select>
          </div>

          {categories.size > 0 && (
            <div className="flex items-center space-x-2 bg-white rounded-lg border border-gray-200 px-3 py-2">
              <FiFilter className="h-4 w-4 text-gray-500" />
              <select
                value={categoryFilter || ''}
                onChange={(e) => setCategoryFilter(e.target.value || null)}
                className="text-sm bg-transparent border-0 focus:outline-none focus:ring-0"
              >
                <option value="">All Categories</option>
                {Array.from(categories).sort().map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-sm text-red-800">
            <strong>Error:</strong> {error}
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block h-8 w-8 border-4 border-blue-300 border-t-blue-600 rounded-full animate-spin mb-2"></div>
            <p className="text-gray-600">Loading standards...</p>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && getFilteredStandards().length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <div className="text-gray-400 mb-3">📋</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">No Standards Found</h3>
          <p className="text-gray-600 text-sm">
            {standards.length === 0
              ? 'Start by creating a new standard'
              : 'No standards match your current filters'}
          </p>
        </div>
      )}

      {/* Pipeline View */}
      {!isLoading && getFilteredStandards().length > 0 && (
        <div className="grid grid-cols-4 gap-4">
          {PIPELINE_STAGES.map(stage => {
            const stageStandards = getStandardsForStage(stage);
            return (
              <div
                key={stage.id}
                className={`rounded-lg border-2 ${stage.borderColor} ${stage.bgColor} p-4 flex flex-col`}
              >
                {/* Stage Header */}
                <div className="mb-4 pb-3 border-b-2 border-gray-200">
                  <h3 className="font-semibold text-gray-900 text-sm">
                    {stage.title}
                  </h3>
                  <p className="text-xs text-gray-600 mt-1">
                    {stage.description}
                  </p>
                  <div className="text-lg font-bold text-gray-900 mt-2">
                    {stageStandards.length}
                  </div>
                </div>

                {/* Standards List */}
                <div className="space-y-3 flex-1 overflow-y-auto max-h-[calc(100vh-350px)]">
                  {stageStandards.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <p className="text-xs">No standards</p>
                    </div>
                  ) : (
                    stageStandards.map(standard => renderStandardCard(standard))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Standard Modal */}
      {showCreateModal && (
        <NewCardModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchStandards();
          }}
          defaultCardType="STANDARD"
          defaultState="draft"
          clientId={effectiveClientId || ''}
        />
      )}
    </div>
  );
};

export default StandardsPage;
